/**
 * The closed four-role set from ADR v3 §3.5 (verbatim).
 */
export type AppRole = 'InternalAdmin' | 'InternalViewer' | 'ClientAdmin' | 'ClientUser';

export const INTERNAL_ROLES: readonly AppRole[] = ['InternalAdmin', 'InternalViewer'];
export const CLIENT_ROLES: readonly AppRole[] = ['ClientAdmin', 'ClientUser'];

/**
 * The four roles most-privileged first. Declared here beside the role sets so
 * every consumer that must pick *one* role out of a session asks the same
 * question the same way — the same reason `isInternalRole` lives here.
 *
 * This is a **presentation** ordering, not an authorization one: visibility is
 * the union of the roles held (FR-006, any-of), and enforcement is API-side.
 * It exists because the roles array is in the issuer's claim order —
 * `recognizedRolesFor` preserves that deliberately and `claims-mapping.spec.ts`
 * pins it — so `roles[0]` is whatever the IdP happened to emit first, which is
 * not a fact about the user (AITH-121 PR review).
 */
export const ROLE_PRECEDENCE: readonly AppRole[] = [
  'InternalAdmin',
  'InternalViewer',
  'ClientAdmin',
  'ClientUser',
];

/**
 * The most privileged role the session holds, or `undefined` for a session with
 * no recognized role (signed out, or denied fail-closed before derivation).
 * Deterministic for a given role *set*, whatever order the issuer listed it in.
 */
export function highestPrecedenceRole(roles: readonly AppRole[]): AppRole | undefined {
  return ROLE_PRECEDENCE.find((role) => roles.includes(role));
}

export interface Session {
  readonly isAuthenticated: boolean;
  readonly roles: readonly AppRole[];
  /** Validated token display data; memory-only and never used for authorization. */
  readonly displayName?: string | null;
  /**
   * Non-null only for client roles. Never persisted to browser storage.
   * Always the canonical form (see client-code-canonicalizer.ts): the session
   * boundary normalizes identity-claim casing so grant, snapshot, and tile
   * comparisons use exact case-sensitive equality (FR-003/FR-008).
   */
  readonly clientCode: string | null;
}

/**
 * True only for an **authenticated** session holding an explicit internal role
 * (`InternalAdmin`/`InternalViewer`). Declared here, beside the role sets it
 * derives from, so every consumer asks the same question the same way
 * (AITH-44 uses it for the client selector's visibility and for the option
 * lists' scope). Fail-closed on purpose: the signed-out session is
 * `{ roles: [] }`, and inferring "staff" from the absence of a client role
 * would classify it as internal during the sign-out window. Hiding UI on this
 * is UX only — the API's 403 is the enforcement (FR-005, SC-007).
 */
export function isInternalRole(session: Session): boolean {
  return session.isAuthenticated && session.roles.some((role) => INTERNAL_ROLES.includes(role));
}

/**
 * True when the session holds a client-portal role. NOT the negation of
 * {@link isInternalRole}: the signed-out and not-yet-signed-in sessions are
 * neither. FR-005's "ignore a URL client parameter" applies to client roles
 * specifically — a deep-link restore runs before sign-in (memory-only OIDC
 * storage), and its client value must survive into a staff session while a
 * client-role session must still never honour it.
 */
export function hasClientRole(session: Session): boolean {
  return session.roles.some((role) => CLIENT_ROLES.includes(role));
}
